import useApi from "../services/ApiService";
import { Paging } from "../models/Paging";
import { MigrationType } from "../models/Statistics/MigrationType";
import { ConfiguredSourceRoot, SourceRootOutcome } from "../models/ConfiguredSourceRoot";
import {
  SourceLink,
  SourceLinkFile,
  SourceLinkOutcome,
  SourceLinkOwner,
  SourceLinkPage,
  SourceLinkReason,
  SourceLinkState,
  SourceLinkSummary,
} from "../models/SourceLink";

export interface GetSourceLinksOptions {
  /** Which table to read. Required: the registry is read one entity type at a time. */
  entityType: MigrationType;
  outcome?: SourceLinkOutcome | null;
  reason?: SourceLinkReason | null;
  /** Who can act on the link. Null for every owner. */
  owner?: SourceLinkOwner | null;
  /** One of the outstanding kinds, healthy, or gone from the source. Null for every state. */
  state?: SourceLinkState | null;
  /** True for links that worked on the previous cycle and do not now; false for the rest; null for both. */
  regressed?: boolean | null;
  /** True for links some cycle has reported failing; false for those none ever has; null for both. */
  failedBefore?: boolean | null;
  search?: string | null;
  /** The cursor the previous page returned. Absent for the first page. */
  after?: string | null;
  pageSize?: number | null;
  descending?: boolean;
}

export interface GetConfiguredSourceRootsOptions {
  migrationType?: MigrationType | null;
  outcome?: SourceRootOutcome | null;
  /** The roots one cycle last reported. A later cycle reporting the same root takes the row with it. */
  discoveryCycleId?: string | null;
}

export class SourceLinksRepository {
  private api = useApi();

  /**
   * One keyset page of one entity type's links. Keyset rather than offset because the healthy view of a large
   * firm runs to tens of millions of rows, and an offset into it re-reads everything before it on every page.
   */
  async getSourceLinks(options: GetSourceLinksOptions): Promise<SourceLinkPage> {
    const {
      entityType,
      outcome = null,
      reason = null,
      owner = null,
      // Null is dropped from the query string, which the server reads as no filter. A page's own default is
      // expressed by the caller passing a value, never by a default here — see GetSourceLinksQuery.State.
      state = null,
      regressed = null,
      failedBefore = null,
      search = null,
      after = null,
      pageSize = null,
      descending = true,
    } = options;

    try {
      const response = await this.api.get("/api/SourceLinks/Links", {
        params: {
          EntityType: entityType,
          Outcome: outcome,
          Reason: reason,
          Regressed: regressed,
          FailedBefore: failedBefore,
          Owner: owner,
          State: state,
          Search: search,
          After: after,
          PageSize: pageSize,
          Descending: descending,
        },
        paramsSerializer: { indexes: true },
      });

      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch source links");
    }
  }

  /** Per entity type, the outstanding links by kind and the latest finished cycle's census. */
  async getSummary(): Promise<SourceLinkSummary[]> {
    try {
      const response = await this.api.get("/api/SourceLinks/Summary");
      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch the source link summary");
    }
  }

  /** The files one link produced, as attributed by the client that discovered them. Offset-paged: one link's files are few. */
  async getLinkFiles(
    entityType: MigrationType,
    linkId: string,
    pageNumber: number,
    pageSize: number
  ): Promise<{ data: SourceLinkFile[]; paging: Paging | null }> {
    try {
      const response = await this.api.get(`/api/SourceLinks/${entityType}/${linkId}/files`, {
        params: { pageNumber, pageSize },
      });
      return { data: response.data, paging: readPaging(response.headers) };
    } catch (error) {
      throw new Error("Failed to fetch the link's files");
    }
  }

  /**
   * The configured roots themselves, which is the only place an unreachable root is named — a cycle's
   * `rootsUnreachable` is a tally and identifies nothing. Unpaged: a firm configures a handful of roots per
   * migration type, and the server caps the result rather than offering pages.
   */
  async getConfiguredSourceRoots(
    options: GetConfiguredSourceRootsOptions = {}
  ): Promise<ConfiguredSourceRoot[]> {
    const {
      migrationType = null,
      outcome = null,
      discoveryCycleId = null,
    } = options;

    try {
      const response = await this.api.get("/api/SourceLinks/Roots", {
        params: {
          MigrationType: migrationType,
          Outcome: outcome,
          DiscoveryCycleId: discoveryCycleId,
        },
      });

      return response.data;
    } catch (error) {
      throw new Error("Failed to fetch configured source roots");
    }
  }
}

function readPaging(headers: Record<string, unknown>): Paging | null {
  const header = headers["x-pagination"];
  if (!header || typeof header !== "string") return null;

  try {
    return JSON.parse(header) as Paging;
  } catch {
    return null;
  }
}

export default SourceLinksRepository;
